import React, { useState } from "react";
import { 
    Box, Typography, TextField, Button, Grid, Paper, 
    FormControlLabel, Radio, RadioGroup, Checkbox, 
    FormGroup, MenuItem, Alert, CircularProgress, Fade, 
    FormHelperText, FormControl, Avatar
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faCheckCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { requestHelp } from "../../../controllers/help/HelpController";

export default function HelpRequestPublicView() {
    const [formData, setFormData] = useState({
        applicant_name: "",
        applicant_ci: "",
        is_third_party: "false", 
        third_party_details: "",
        phone: "",
        city: "",
        location: "",
        family_size: "",
        has_vulnerable_population: "false",
        required_items: [],
        medical_details: "",
        urgency_level: ""
    });

    const [customCity, setCustomCity] = useState("");
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({ loading: false, error: null, success: false });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => {
            const currentItems = [...prev.required_items];
            const newItems = checked 
                ? [...currentItems, value] 
                : currentItems.filter(item => item !== value);
            
            if (errors.required_items && newItems.length > 0) {
                setErrors(prevErrors => ({ ...prevErrors, required_items: null }));
            }
            
            return { ...prev, required_items: newItems };
        });
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.applicant_name.trim()) newErrors.applicant_name = "El nombre es obligatorio.";
        if (!/^[VEve][0-9]{7,8}$/.test(formData.applicant_ci)) newErrors.applicant_ci = "Formato inválido. Ej: V12345678";
        
        if (formData.is_third_party === "true" && !formData.third_party_details.trim()) {
            newErrors.third_party_details = "Debe indicar los datos del tercero afectado.";
        }

        if (!/^58(414|424|412|422|416|426)[0-9]{7}$/.test(formData.phone)) {
            newErrors.phone = "Debe ser 58 + Operadora + Número (Ej: 584241234567).";
        }

        if (!formData.city) newErrors.city = "Seleccione una ciudad.";
        if (formData.city === "Otro" && !customCity.trim()) newErrors.customCity = "Por favor especifique la ciudad.";

        if (!formData.location.trim()) newErrors.location = "La dirección es obligatoria.";
        if (!formData.family_size) newErrors.family_size = "Seleccione el tamaño de la familia.";
        
        if (formData.required_items.length === 0) {
            newErrors.required_items = "Debe seleccionar al menos un tipo de ayuda.";
        }

        if (formData.required_items.includes("Medicamentos") && !formData.medical_details.trim()) {
            newErrors.medical_details = "Por favor especifique qué medicamento necesita.";
        }

        if (!formData.urgency_level) newErrors.urgency_level = "Seleccione el nivel de urgencia actual.";

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const formErrors = validateForm();
        
        if (Object.keys(formErrors).length > 0) {
            const firstErrorField = Object.keys(formErrors)[0];
            const errorElement = document.getElementsByName(firstErrorField)[0];
            
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }
            return;
        }

        setStatus({ loading: true, error: null, success: false });

        const payload = {
            ...formData,
            city: formData.city === "Otro" ? customCity : formData.city,
            is_third_party: formData.is_third_party === "true",
            has_vulnerable_population: formData.has_vulnerable_population === "true",
        };

        try {
            const response = await requestHelp(payload);
            if(response.success === true)
            {
                setStatus({ loading: false, error: null, success: true });
            }
            else
                setStatus({ loading: false, error: response.message, success: null });
            // await api.post('/help-requests', payload);
            //await new Promise(resolve => setTimeout(resolve, 1500));
            //setStatus({ loading: false, error: null, success: true });
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Ocurrió un error de conexión. Por favor, intenta de nuevo.";
            setStatus({ loading: false, error: errorMsg, success: false });
        }
    };

    if (status.success) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8', p: 3 }}>
                <Fade in={true}>
                    <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, textAlign: 'center', maxWidth: 500 }}>
                        <FontAwesomeIcon icon={faCheckCircle} size="4x" color="#2e7d32" style={{ marginBottom: '20px' }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 2 }}>
                            Solicitud Recibida
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Hemos registrado tu información correctamente. Nuestro equipo de supervisores la evaluará a la brevedad y se pondrá en contacto contigo.
                        </Typography>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={() => window.location.reload()}
                            sx={{ bgcolor: '#1a1a1a', py: 1.5, borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                        >
                            Registrar otra solicitud
                        </Button>
                    </Paper>
                </Fade>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8',mt:5, py: { xs: 4, md: 8 }, px: { xs: 2, md: 0 } }}>
            <Box sx={{ maxWidth: 700, mx: 'auto' }}>
                
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar sx={{ bgcolor: '#1565c0', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <FontAwesomeIcon icon={faHeart} size="lg" />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1, letterSpacing: -0.5 }}>
                        Solicitud de Ayuda Humanitaria
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Centro de acopio Playa Mansa. Por favor, completa este formulario para canalizar el apoyo a quienes lo necesitan.
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit} noValidate>
                    
                    {/* BLOQUE 1: IDENTIFICACIÓN */}
                    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>1. Datos del Solicitante</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth label="Nombre y Apellido" name="applicant_name" 
                                    value={formData.applicant_name} onChange={handleChange} 
                                    error={!!errors.applicant_name} helperText={errors.applicant_name}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth label="Cédula de Identidad" name="applicant_ci" 
                                    placeholder="Ej: V12345678" value={formData.applicant_ci} onChange={handleChange} 
                                    error={!!errors.applicant_ci} helperText={errors.applicant_ci || "Debe comenzar con V o E"}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>¿Para quién es la ayuda?</Typography>
                                <RadioGroup row name="is_third_party" value={formData.is_third_party} onChange={handleChange}>
                                    <FormControlLabel value="false" control={<Radio />} label="Para mi núcleo familiar" />
                                    <FormControlLabel value="true" control={<Radio />} label="Reporto el caso de un tercero" />
                                </RadioGroup>
                            </Grid>
                            
                            {formData.is_third_party === "true" && (
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth multiline rows={2} name="third_party_details" 
                                        label="Datos de la persona afectada" placeholder="Nombre, apellido y número (si los conoce)" 
                                        value={formData.third_party_details} onChange={handleChange} 
                                        error={!!errors.third_party_details} helperText={errors.third_party_details}
                                        sx={{ bgcolor: '#f9f9f9' }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {/* BLOQUE 2: CONTACTO Y UBICACIÓN */}
                    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>2. Contacto y Ubicación</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth label="Teléfono (WhatsApp)" name="phone" 
                                    placeholder="584141234567" value={formData.phone} onChange={handleChange} 
                                    error={!!errors.phone} helperText={errors.phone || "Código 58 + Operadora + Número"}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    select fullWidth label="Ciudad" name="city" 
                                    value={formData.city} onChange={handleChange}
                                    error={!!errors.city} helperText={errors.city}
                                >
                                    <MenuItem value="Barcelona">Barcelona</MenuItem>
                                    <MenuItem value="Lechería">Lechería</MenuItem>
                                    <MenuItem value="Puerto La Cruz">Puerto La Cruz</MenuItem>
                                    <MenuItem value="Guanta">Guanta</MenuItem>
                                    <MenuItem value="Otro">Otro</MenuItem>
                                </TextField>
                            </Grid>

                            {formData.city === "Otro" && (
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth label="Especifique su ciudad" name="customCity" 
                                        value={customCity} 
                                        onChange={(e) => { 
                                            setCustomCity(e.target.value); 
                                            if (errors.customCity) setErrors(prev => ({ ...prev, customCity: null })); 
                                        }} 
                                        error={!!errors.customCity} helperText={errors.customCity}
                                        sx={{ bgcolor: '#fff8e1' }} 
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth multiline rows={2} label="Dirección Exacta y Punto de Referencia" 
                                    name="location" value={formData.location} onChange={handleChange} 
                                    error={!!errors.location} helperText={errors.location}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* BLOQUE 3: SITUACIÓN Y NECESIDADES */}
                    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>3. Situación y Necesidades</Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                <FormControl error={!!errors.family_size}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tamaño del grupo familiar</Typography>
                                    <RadioGroup name="family_size" value={formData.family_size} onChange={handleChange}>
                                        <FormControlLabel value="1-3" control={<Radio size="small"/>} label="1 a 3 personas" />
                                        <FormControlLabel value="4-6" control={<Radio size="small"/>} label="4 a 6 personas" />
                                        <FormControlLabel value="7+" control={<Radio size="small"/>} label="7 o más personas" />
                                    </RadioGroup>
                                    {errors.family_size && <FormHelperText>{errors.family_size}</FormHelperText>}
                                </FormControl>

                                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>¿Hay población vulnerable?</Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Niños, adultos mayores, embarazadas o personas con discapacidad.
                                </Typography>
                                <RadioGroup row name="has_vulnerable_population" value={formData.has_vulnerable_population} onChange={handleChange}>
                                    <FormControlLabel value="true" control={<Radio size="small"/>} label="Sí" />
                                    <FormControlLabel value="false" control={<Radio size="small"/>} label="No" />
                                </RadioGroup>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl error={!!errors.required_items} component="fieldset">
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tipo de ayuda requerida</Typography>
                                    <FormGroup name="required_items">
                                        {["Alimentación", "Agua potable", "Fórmulas infantiles / Pañales", "Medicamentos", "Ropa y calzado", "Artículos de refugio"].map(item => (
                                            <FormControlLabel 
                                                key={item} 
                                                control={<Checkbox size="small" value={item} checked={formData.required_items.includes(item)} onChange={handleCheckboxChange} />} 
                                                label={item} 
                                            />
                                        ))}
                                    </FormGroup>
                                    {errors.required_items && <FormHelperText>{errors.required_items}</FormHelperText>}
                                </FormControl>

                                {formData.required_items.includes("Medicamentos") && (
                                    <TextField 
                                        fullWidth size="small" name="medical_details" sx={{ mt: 2, bgcolor: '#f9f9f9' }}
                                        label="¿Qué medicamento requiere?" value={formData.medical_details} onChange={handleChange} 
                                        error={!!errors.medical_details} helperText={errors.medical_details}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl error={!!errors.urgency_level} fullWidth>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Nivel de urgencia actual</Typography>
                                    <RadioGroup name="urgency_level" value={formData.urgency_level} onChange={handleChange}>
                                        <FormControlLabel value="critica" control={<Radio sx={{color: '#d32f2f'}}/>} label={<Typography variant="body2"><strong>Crítica:</strong> Sin alimentos/agua o riesgo de salud inmediato hoy.</Typography>} />
                                        <FormControlLabel value="urgente" control={<Radio sx={{color: '#f57f17'}}/>} label={<Typography variant="body2"><strong>Urgente:</strong> Cuenta con insumos para 24 a 48 horas.</Typography>} />
                                        <FormControlLabel value="preventiva" control={<Radio color="primary"/>} label={<Typography variant="body2"><strong>Preventiva:</strong> Estables, pero sufrieron daños materiales.</Typography>} />
                                    </RadioGroup>
                                    {errors.urgency_level && <FormHelperText>{errors.urgency_level}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {status.error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{status.error}</Alert>
                    )}

                    <Button 
                        type="submit" variant="contained" fullWidth disabled={status.loading}
                        sx={{ bgcolor: '#1565c0', py: 2, borderRadius: 3, fontSize: '1.1rem', fontWeight: 800, textTransform: 'none' }}
                    >
                        {status.loading ? <CircularProgress size={28} color="inherit" /> : "Enviar Solicitud de Ayuda"}
                    </Button>
                    
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 3, color: '#888' }}>
                        <FontAwesomeIcon icon={faInfoCircle} /> Sus datos serán tratados con estricta confidencialidad para fines logísticos.
                    </Typography>
                </form>
            </Box>
        </Box>
    );
}